'use client';

import { gql } from '@apollo/client';
import React from 'react';
import { When } from 'react-if';

import classes from '@nerdwallet/base-styles/classes';
import { Accent } from '@nerdwallet/currency/components/Accent';
import { Box } from '@nerdwallet/currency/components/Box';
import DownIcon from '@nerdwallet/react-icon/material/ArrowDownward';
import UpIcon from '@nerdwallet/react-icon/material/ArrowUpward';

import {
  UserInsightInterface,
  UserInsightSentimentEnum,
} from '@generated-gql/generated-types';
import { SegmentEventViewed } from '@lib/segment';

import styles from './user-insight-card.module.css';
import { getSegmentPayload } from './utils';

interface Component<E> extends React.FC<E> {
  fragment: any;
}

interface Props {
  analyticsPayload: {
    elementPosition: number;
    moduleName: string;
    sectionPosition: number;
  };
  userInsight: UserInsightInterface;
}

const sentimentConfigMap = {
  [UserInsightSentimentEnum.Cautionary]: {
    ArrowIcon: DownIcon,
    backgroundColor: 'red-lightest',
    textColor: 'color-red',
  },
  [UserInsightSentimentEnum.Positive]: {
    ArrowIcon: UpIcon,
    backgroundColor: 'green-lightest',
    textColor: 'color-green',
  },
};

const UserInsightCard: Component<Props> = ({
  analyticsPayload,
  userInsight,
}) => {
  const { gain, sentiment } = userInsight;
  const sentimentConfig = sentimentConfigMap[sentiment];
  const segmentPayload = getSegmentPayload(userInsight, {
    meaningfulEngagementCategory: 'Credit Score',
    ...analyticsPayload,
  });

  return (
    <SegmentEventViewed
      className={classes('height-100')}
      eventName="UIA Insight Viewed"
      eventPayload={segmentPayload}
    >
      <Box
        borderRadius="2"
        className={styles.overflowHidden}
        display="flex"
        flexDirection="column"
        height="100"
        shadow="1"
      >
        <When condition={!!sentimentConfig}>
          <Box
            alignItems="center"
            backgroundColor={sentimentConfig?.backgroundColor}
            className={classes(sentimentConfig?.textColor)}
            display="flex"
            gridGap="1"
            justifyContent="center"
            paddingY="1"
          >
            {sentimentConfig?.ArrowIcon ? (
              <sentimentConfig.ArrowIcon size={16} />
            ) : null}
            <Accent size="small">{gain?.description}</Accent>
          </Box>
        </When>
        <Box
          backgroundColor="white"
          className={classes('flex-1')}
          display="flex"
          flexDirection="column"
          gridGap="3"
          justifyContent={'flex-start'}
          padding="3-half"
        >
          <Box backgroundColor="neutral-lighter-2" padding="2" width="100" />
          <Box backgroundColor="neutral-lighter-2" padding="2" width="80" />
        </Box>
      </Box>
    </SegmentEventViewed>
  );
};

UserInsightCard.fragment = gql`
  fragment creditScoreUserInsightCardFragment on UserInsightInterface {
    gain {
      description
    }
    sentiment
    ...getSegmentPayloadFragment
  }
  ${getSegmentPayload.fragment}
`;

export default UserInsightCard;
